import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Text, useToggle } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  BankingEntitiesPaymentStatusEnum,
  PaymentRequestDetailFieldsFragment,
  PaymentRequestDetailItemsDocument,
  PaymentRequestDetailRoutesDocument,
  PaymentRequestItemFieldsFragment,
  PaymentRequestItemsDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { PayPaymentRequestsInBulkModal } from 'modules/banking/components/PayPaymentRequestsInBulkModal'
import {
  PaymentRequestItemDetailSidePanel,
  type PaymentRequestItemData,
} from 'modules/banking/components/PaymentRequestItemDetailSidePanel'
import { PaymentRequestItemTransfersSidePanel } from 'modules/banking/components/PaymentRequestItemTransfersSidePanel'
import { PaymentRequestInsightCards } from 'modules/banking/components/TreasuryInsightCard'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { BulkPaymentsTimeline } from './BulkPaymentsTimeline'

type StatusColor = 'neutral' | 'info' | 'positive' | 'warning' | 'critical'

const getItemStatusColor = (status: string): StatusColor => {
  const statusColorMap: Record<string, StatusColor> = {
    init: 'warning',
    in_payment: 'info',
    in_progress: 'info',
    paid: 'positive',
    rejected: 'critical',
  }
  return statusColorMap[status] ?? 'neutral'
}

const getItemStatusLabel = (status: string): string => {
  const statusLabelMap: Record<string, string> = {
    init: i18n.t('banking.paymentRequests.itemStatus.init'),
    in_payment: i18n.t('banking.paymentRequests.itemStatus.inPayment'),
    in_progress: i18n.t('banking.paymentRequests.itemStatus.inProgress'),
    paid: i18n.t('banking.paymentRequests.itemStatus.paid'),
    rejected: i18n.t('banking.paymentRequests.itemStatus.rejected'),
  }
  return statusLabelMap[status] ?? status
}

interface BulkPaymentInfo {
  id: number
  label: string
  status: BankingEntitiesPaymentStatusEnum
  date: string
}

const PaymentRequestDetailPage = () => {
  const { paymentRequestId } = useParams<{ paymentRequestId: string }>()
  const {
    access: { locale },
    company,
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [selectedItemId, setSelectedItemId] = useState<number | null>(null)
  const [selectedBeneficiaryName, setSelectedBeneficiaryName] = useState<string>('')
  const [selectedItemData, setSelectedItemData] = useState<PaymentRequestItemData | null>(null)
  const [sidePanelOpen, toggleSidePanel] = useToggle(false)
  const [detailSidePanelOpen, toggleDetailSidePanel] = useToggle(false)
  const [sendToPayModalOpen, toggleSendToPayModal] = useToggle(false)

  // Query for payment request data (for send to pay modal)
  const { data: paymentRequestData } = useQuery(PaymentRequestDetailRoutesDocument, {
    variables: {
      id: Number(paymentRequestId),
      companyId: company.id,
      featureNames: [],
    },
    skip: !paymentRequestId,
  })

  // Query for bulk payments timeline
  const { data: detailData } = useQuery(PaymentRequestDetailItemsDocument, {
    variables: {
      paymentRequestId: Number(paymentRequestId),
    },
    skip: !paymentRequestId,
  })

  const paymentRequest: PaymentRequestDetailFieldsFragment | null =
    paymentRequestData?.banking?.paymentRequest ?? null

  // Extract unique bulk payments from transfers for the timeline
  const bulkPayments: BulkPaymentInfo[] = useMemo(() => {
    const edges = detailData?.banking?.paymentRequestItemsConnection?.edges ?? []
    const paymentsMap = new Map<number, BulkPaymentInfo>()

    edges.forEach((edge) => {
      const transfers = edge.node.transfersConnection?.edges ?? []
      transfers.forEach((transferEdge) => {
        const payment = transferEdge.node.paymentExecution?.payment
        if (payment && !paymentsMap.has(payment.id)) {
          paymentsMap.set(payment.id, {
            id: payment.id,
            label: payment.label,
            status: payment.status,
            date: payment.date,
          })
        }
      })
    })

    return Array.from(paymentsMap.values())
  }, [detailData])

  // Get currency from detailData
  const currency =
    detailData?.banking?.paymentRequestItemsConnection?.edges?.[0]?.node.paymentRequest?.currency ??
    'EUR'

  // DataCollection source for items table
  const dataSource = useGraphqlDataCollectionSource({
    document: PaymentRequestItemsDataCollectionDocument,
    mapData: (data) =>
      data.banking.paymentRequestItemsConnection.edges.map(
        (edge: { node: PaymentRequestItemFieldsFragment }) => edge.node
      ),
    mapVariables: () => ({
      bankingPaymentRequestIds: paymentRequestId ? [Number(paymentRequestId)] : [],
    }),
    paginationType: 'pages',
    perPage: 10,
    mapPagination: (data) => ({
      total: data.banking.paymentRequestItemsConnection.totalCount ?? 0,
      hasNextPage: data.banking.paymentRequestItemsConnection.pageInfo.hasNextPage ?? false,
    }),
    itemOnClick: (item: PaymentRequestItemFieldsFragment) => () => {
      const hasTransfers =
        item.status !== 'init' && (item.transfersConnection.totalCount ?? 0) > 0

      if (hasTransfers) {
        setSelectedItemId(item.id)
        setSelectedBeneficiaryName(item.beneficiaryName)
        toggleSidePanel(true)()
      } else {
        setSelectedItemData({
          beneficiaryName: item.beneficiaryName,
          beneficiaryAccountNumber: item.beneficiaryAccountNumber,
          amountCents: item.amountCents,
          currency: item.paymentRequest?.currency ?? 'EUR',
          status: item.status,
          createdAt: item.createdAt,
          dueDate: item.paymentRequest?.dueDate,
          sourceId: item.sourceId,
          sourceType: item.sourceType,
        })
        toggleDetailSidePanel(true)()
      }
    },
  })

  const columns = useMemo(
    () => [
      {
        label: i18n.t('banking.paymentRequests.itemColumns.beneficiary'),
        render: (row: PaymentRequestItemFieldsFragment) => ({
          type: 'text' as const,
          value: row.beneficiaryName,
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.itemColumns.account'),
        render: (row: PaymentRequestItemFieldsFragment) => ({
          type: 'text' as const,
          value: row.beneficiaryAccountNumber,
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.itemColumns.amount'),
        render: (row: PaymentRequestItemFieldsFragment) => ({
          type: 'text' as const,
          value: MoneyWithCurrency({
            amount: row.amountCents,
            currency: row.paymentRequest?.currency ?? 'EUR',
            locale,
          }),
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.itemColumns.status'),
        render: (row: PaymentRequestItemFieldsFragment) => ({
          type: 'status' as const,
          value: {
            label: getItemStatusLabel(row.status),
            status: getItemStatusColor(row.status),
          },
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.itemColumns.attempts'),
        render: (row: PaymentRequestItemFieldsFragment) => ({
          type: 'text' as const,
          value:
            (row.transfersConnection.totalCount ?? 0) > 0
              ? String(row.transfersConnection.totalCount)
              : '-',
        }),
      },
    ],
    [locale]
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns,
        },
      },
    ],
    [columns]
  )

  const goToPaymentDetail = (paymentId?: number) => {
    if (paymentId) {
      navigate(
        navigation.finance.treasury.banking.payments.paymentId.fullPath({
          id: String(paymentId),
        })
      )
    } else {
      navigate(navigation.finance.treasury.banking.payments.fullPath())
    }
  }

  const handleCloseSidePanel = () => {
    toggleSidePanel(false)()
    setSelectedItemId(null)
    setSelectedBeneficiaryName('')
  }

  const handleCloseDetailSidePanel = () => {
    toggleDetailSidePanel(false)()
    setSelectedItemData(null)
  }

  const handlePaymentClick = (paymentId: number) => {
    navigate(
      navigation.finance.treasury.banking.payments.paymentId.fullPath({
        id: String(paymentId),
      })
    )
  }

  const handleActionClick = (key: string) => {
    if (key === 'send_to_pay') {
      toggleSendToPayModal(true)()
    }
  }

  const sideContent = (
    <Box flexDirection="column" height="full">
      <Box padding="s16" style={{ borderBottom: '1px solid var(--grey200)' }}>
        <Text.Normal weight="semibold" size="200" color="grey700">
          {i18n.t('banking.paymentRequests.detail.bulkPaymentsTitle').toUpperCase()}
        </Text.Normal>
      </Box>
      <Box padding="s16" style={{ overflow: 'auto', flex: '1' }}>
        <BulkPaymentsTimeline
          payments={bulkPayments}
          locale={locale}
          onPaymentClick={handlePaymentClick}
        />
      </Box>
    </Box>
  )

  return (
    <ResponsivePage.Body variant="fullscreen" onActionClick={handleActionClick}>
      <TwoColumnLayout sideContent={sideContent}>
        <Box flexDirection="column" gap="s24" height="full">
          <PaymentRequestInsightCards
            paymentRequestId={Number(paymentRequestId)}
            currency={currency}
            locale={locale}
          />
          <Box height="full">
            <OneDataCollection
              source={dataSource}
              visualizations={visualizations}
              fullHeight
              emptyStates={{
                'no-data': {
                  title: i18n.t('banking.paymentRequests.detail.emptyState.title'),
                  description: i18n.t('banking.paymentRequests.detail.emptyState.description'),
                },
              }}
            />
          </Box>
        </Box>
      </TwoColumnLayout>

      {/* Side Panel for Transfer Details */}
      {sidePanelOpen && selectedItemId && (
        <PaymentRequestItemTransfersSidePanel
          paymentRequestItemId={selectedItemId}
          beneficiaryName={selectedBeneficiaryName}
          onClose={handleCloseSidePanel}
        />
      )}

      {/* Side Panel for Item Details */}
      {detailSidePanelOpen && selectedItemData && (
        <PaymentRequestItemDetailSidePanel
          item={selectedItemData}
          onClose={handleCloseDetailSidePanel}
        />
      )}

      {/* Send to Pay Modal */}
      {sendToPayModalOpen && paymentRequest && (
        <PayPaymentRequestsInBulkModal
          onSuccess={goToPaymentDetail}
          onClose={() => toggleSendToPayModal(false)()}
          paymentRequests={[paymentRequest]}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default PaymentRequestDetailPage
